<template>
  <b-modal
    :id="id"
    hide-header
    hide-footer
    v-on:hide="hideEvent"
    :centered="centered"
    no-close-on-backdrop
  >
    <template #default="{ ok, cancel }">
      <b-row v-if="!help" class="alert-message" no-gutters>
        {{ msg }}
      </b-row>
      <b-row v-else no-gutters>
        <!-- 掲示板は20を指定するとのこと -->
        <help-modal-content
          :title="helpContentArgs.title"
          :content="helpContentArgs.content"
          :isError="helpContentArgs.isError"
          v-on:reload="reload()"
        />
      </b-row>

      <b-row class="alert-footer" no-gutters>
        <cancel
          v-if="!hideCancel"
          class="col-5"
          :label="cancelLabel"
          v-on:cancel="clickedCancel(cancel)"
        />

        <!-- help表示の時、Infoボタンを出して遷移させる -->
        <b-button
          variant="info"
          v-if="help"
          class="col-5"
          :href="helpUrl"
          target="_blank"
          rel="noopener noreferrer"
          >{{ helpButtonText }}</b-button
        >

        <confirm class="col-5" :label="okLabel" :variant="okVariant" v-on:confirm="clickedOK(ok)" />
      </b-row>
    </template>
  </b-modal>
</template>

<style lang="scss" scoped>
.alert-message {
  font-size: 16px;
  font-weight: bold;
  white-space: pre-line;
}

.alert-footer {
  margin-top: 50px;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
}
</style>

<script lang="ts">
import { defineComponent, getCurrentInstance, onMounted, type PropType } from "vue";
import type Vue from "vue";
import Confirm from "./button/Confirm.vue";
import Cancel from "./button/Cancel.vue";

// HelpContent が npmから使えるような仕組みになっていないため(Vueへの登録などが無い)、しばらくはコードコピーしたものを使うことにする
import HelpModalContent from "@lisb/dx-flow-help";
import "@lisb/dx-flow-help/dist/dx-flow-help.css";

import { useLegacyRootEvent } from "../composables/useLegacyRootEvent";

const HELP_URL = "https://direct4b.com/ja/plan/";
const HELP_BUTTON_LABEL = "料金プラン";

export default defineComponent({
  name: "alert-modal",
  components: { Confirm, Cancel, HelpModalContent },
  props: {
    id: { type: String, default: "", required: true },
    msg: { type: String, default: "", required: true },
    cancelLabel: { type: String, default: "キャンセル" },
    okLabel: { type: String, default: "OK" },
    okVariant: { type: String, default: "info" },
    centered: { type: Boolean, default: true },
    /** true: キャンセルボタンを出さない false: キャンセルボタンを出す */
    hideCancel: { type: Boolean, default: false },
    // true: ヘルプ文言を出す false: 出さない
    help: { type: Boolean, default: false },
    helpContentArgs: {
      type: Object as PropType<{
        title: string;
        content: string;
        isError: boolean;
      }>,
      default: () => ({ title: "", content: "", isError: true }),
    },
    // Storybook向け
    show: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    // $bvModal は BootstrapVue が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;
    const rootEvent = useLegacyRootEvent();

    // ヘルプ関係
    const helpUrl = HELP_URL;
    const helpButtonText = HELP_BUTTON_LABEL;

    // cancel clicked
    const clickedCancel = (cancelEvent: () => void): void => {
      emit("clickedCancel");
      cancelEvent();
    };

    // OK clicked
    const clickedOK = (okEvent: () => void): void => {
      emit("clickedOK");
      okEvent();
    };

    // モーダルが閉じた際の処理
    const hideEvent = (): void => {
      emit("hideModal");
    };

    const reload = (): void => {
      // URLを再取得する
      rootEvent.emit("free-alert");
    };

    onMounted(() => {
      if (props.show) {
        vm?.$bvModal.show(props.id);
      }
    });

    return {
      helpUrl,
      helpButtonText,
      clickedCancel,
      clickedOK,
      hideEvent,
      reload,
    };
  },
});
</script>
